import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  User,
  Briefcase,
  FileText,
  MapPin,
  Phone,
  Mail,
  Shield,
  DollarSign,
  Calendar,
  Building2,
  Loader2,
  ExternalLink,
  Download,
  StickyNote,
  MessageSquare,
  Save
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { useApplication } from '../../hooks/useApplication';
import { supabase } from '../../lib/supabaseClient';

const ApplicationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { application, loading, error, refetch } = useApplication(id);
  const [updating, setUpdating] = useState(false);
  const [statusReason, setStatusReason] = useState('');
  const [internalNotes, setInternalNotes] = useState('');
  const [clientFeedback, setClientFeedback] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved] = useState(false);

  useEffect(() => {
    if (application) {
      setInternalNotes(application.internal_notes || '');
      setClientFeedback(application.client_feedback || '');
    }
  }, [application]);

  const handleStatusChange = async (newStatus) => {
    try {
      setUpdating(true);
      const { error } = await supabase
        .from('applications')
        .update({
          status: newStatus,
          status_reason: statusReason.trim() || null
        })
        .eq('id', id);

      if (error) throw error;

      setStatusReason('');
      await refetch();
    } catch (err) {
      console.error('Error updating application status:', err);
      alert('Failed to update status. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setSavingNotes(true);
      const { error } = await supabase
        .from('applications')
        .update({
          internal_notes: internalNotes.trim() || null,
          client_feedback: clientFeedback.trim() || null
        })
        .eq('id', id);

      if (error) throw error;

      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
      await refetch();
    } catch (err) {
      console.error('Error saving notes:', err);
      alert('Failed to save notes. Please try again.');
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground">Loading application...</p>
        </div>
      </div>
    );
  }

  if (error || !application) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-red-600">Error loading application: {error || 'Not found'}</p>
          <Button onClick={() => navigate('/recruiter/rfqs')} className="mt-4">
            Back to RFQs
          </Button>
        </div>
      </div>
    );
  }

  const { candidate, rfq } = application;

  // Helper to format locations
  const formatLocations = (locationsStr) => {
    if (!locationsStr) return 'Not specified';
    return locationsStr.split(',').map(loc => loc.trim()).join(', ');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold">
              {candidate?.first_name} {candidate?.last_name}
            </h1>
            <p className="text-muted-foreground">
              Application for {rfq?.ref_code} - {rfq?.title}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/recruiter/candidates/${application.candidate_id}`)}
          >
            View Candidate Profile
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate(`/recruiter/rfqs/${application.rfq_id}`)}
          >
            View RFQ
          </Button>
        </div>
      </div>

      {/* Status and Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Application Status</span>
            <Badge variant={
              application.status === 'accepted' ? 'default' :
              application.status === 'rejected' ? 'destructive' :
              application.status === 'shortlisted' ? 'secondary' :
              'outline'
            }>
              {application.status}
            </Badge>
          </CardTitle>
          <CardDescription>
            Submitted on {new Date(application.created_at).toLocaleDateString('en-AU', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-sm font-medium block mb-1">Reason (optional, applied to next status change)</label>
            <input
              type="text"
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
              placeholder="e.g. Client selected another candidate on rate"
              className="w-full max-w-xl border rounded-md px-3 py-1.5 text-sm bg-background"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <label className="text-sm font-medium">Update Status:</label>
            <div className="flex flex-wrap gap-2">
              {['submitted', 'reviewing', 'shortlisted', 'interviewing', 'offered', 'accepted', 'rejected', 'unsuitable', 'withdrawn'].map((status) => (
                <Button
                  key={status}
                  size="sm"
                  variant={application.status === status ? 'default' : 'outline'}
                  onClick={() => handleStatusChange(status)}
                  disabled={updating || application.status === status}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>
          </div>
          {application.status_reason && (
            <p className="text-sm text-muted-foreground">
              Last reason recorded: <span className="italic">{application.status_reason}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Notes & Feedback */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <StickyNote className="h-5 w-5 mr-2" />
            Notes &amp; Feedback
          </CardTitle>
          <CardDescription>
            Internal notes are only visible to recruiters. Candidate feedback is shown to the candidate on
            their applications page if their status is rejected or unsuitable.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium block mb-1 flex items-center">
              <StickyNote className="h-4 w-4 mr-1" />
              Internal Notes (recruiter only)
            </label>
            <Textarea
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              rows={3}
              placeholder="Notes for your own reference..."
            />
          </div>
          <div>
            <label className="text-sm font-medium block mb-1 flex items-center">
              <MessageSquare className="h-4 w-4 mr-1" />
              Candidate Feedback (visible to candidate)
            </label>
            <Textarea
              value={clientFeedback}
              onChange={(e) => setClientFeedback(e.target.value)}
              rows={3}
              placeholder="What the candidate will see if they view feedback on this application..."
            />
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={handleSaveNotes} disabled={savingNotes} size="sm">
              <Save className={`h-4 w-4 mr-2 ${savingNotes ? 'animate-spin' : ''}`} />
              {savingNotes ? 'Saving...' : 'Save Notes'}
            </Button>
            {notesSaved && <span className="text-sm text-green-600">Saved</span>}
          </div>
        </CardContent>
      </Card>

      {/* Candidate Personal Information - Summary Block */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <User className="h-5 w-5 mr-2" />
            Candidate Information
          </CardTitle>
          <CardDescription>Personal details from candidate profile</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Name</p>
                <p className="font-medium">{candidate?.first_name} {candidate?.last_name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                  <Mail className="h-4 w-4 mr-1" />
                  Email
                </p>
                <p>{candidate?.email || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                  <Phone className="h-4 w-4 mr-1" />
                  Phone
                </p>
                <p>{candidate?.phone || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Location
                </p>
                <p>
                  {candidate?.location_city && candidate?.location_state
                    ? `${candidate.location_city}, ${candidate.location_state}`
                    : candidate?.location_city || candidate?.location_state || 'Not provided'}
                  {candidate?.location_country && ` (${candidate.location_country})`}
                </p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                  <Shield className="h-4 w-4 mr-1" />
                  Security Clearance
                </p>
                <p>
                  {candidate?.clearance_level ? (
                    <Badge variant="outline">{candidate.clearance_level}</Badge>
                  ) : (
                    'None'
                  )}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Citizenship Status</p>
                <p>{candidate?.citizenship_status || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Payroll Preference</p>
                <p>{candidate?.payroll_type || 'Not specified'}</p>
              </div>
              {candidate?.pty_ltd_abn && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">ABN Number</p>
                  <p>{candidate.pty_ltd_abn}</p>
                </div>
              )}
              {candidate?.linkedin_url && (
                <div>
                  <p className="text-sm font-medium text-muted-foreground mb-1">LinkedIn</p>
                  <a
                    href={candidate.linkedin_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline flex items-center gap-1"
                  >
                    View Profile <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Application-Specific Details */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Briefcase className="h-5 w-5 mr-2" />
            Application Details
          </CardTitle>
          <CardDescription>Information specific to this application</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                  <DollarSign className="h-4 w-4 mr-1" />
                  Asking Rate
                </p>
                <p className="font-medium">
                  {application.asking_rate_value
                    ? `$${application.asking_rate_value} per ${application.asking_rate_basis}`
                    : 'Not specified'
                  }
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                  <Calendar className="h-4 w-4 mr-1" />
                  Notice Period / Availability
                </p>
                <p>{application.notice_period || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                  <MapPin className="h-4 w-4 mr-1" />
                  Locations Available to Work
                </p>
                <p>{formatLocations(application.locations_available)}</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1">Authority to Represent</p>
                <Badge variant={application.authority_to_represent ? 'default' : 'secondary'}>
                  {application.authority_to_represent ? 'Yes' : 'No'}
                </Badge>
              </div>
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-1 flex items-center">
                  <Building2 className="h-4 w-4 mr-1" />
                  Applying for
                </p>
                <div className="space-y-1">
                  <p className="font-medium">{rfq?.title}</p>
                  <p className="text-sm text-muted-foreground">{rfq?.client?.name}</p>
                  <p className="text-sm text-muted-foreground">Ref: {rfq?.ref_code}</p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Documents */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <FileText className="h-5 w-5 mr-2" />
            Submitted Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {application.resume_url && (
              <div className="flex items-center justify-between p-3 bg-accent rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Resume</span>
                </div>
                <a
                  href={application.resume_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  <Download className="h-4 w-4" />
                  Download
                </a>
              </div>
            )}
            {application.cover_letter_url && (
              <div className="flex items-center justify-between p-3 bg-accent rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Selection Criteria / Cover Letter</span>
                </div>
                <a
                  href={application.cover_letter_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-primary hover:underline"
                >
                  <Download className="h-4 w-4" />
                  Download
                </a>
              </div>
            )}
            {!application.resume_url && !application.cover_letter_url && (
              <p className="text-muted-foreground">No documents submitted</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Selection Criteria Response */}
      {application.selection_criteria_response && (
        <Card>
          <CardHeader>
            <CardTitle>Selection Criteria Response</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="prose max-w-none">
              <p className="whitespace-pre-wrap">{application.selection_criteria_response}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Custom Questions */}
      {application.additional_info && application.additional_info.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Questions</CardTitle>
            <CardDescription>Client-specific questions and responses</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {application.additional_info.map((item, index) => (
                <div key={index} className="border-b pb-4 last:border-b-0">
                  <p className="font-medium mb-2">{item.question_text}</p>
                  <p className="text-muted-foreground whitespace-pre-wrap">{item.answer || 'No answer provided'}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ApplicationDetail;
